import { todayCalendarDate } from '@shared/dates/calendarDate';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CloudUpload, Download, SelectAll, TrendingUp } from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { DataTable } from '../components/data-table';
import { LoadingState } from '../components/loading-state';
import { ProspectsUploadModal } from '../components/prospects-upload-modal';
import type { ProspectResponse } from '../store/api';
import { useGetProspectsQuery } from '../store/hooks';
import { adminV2Api } from '../store/api';
import { formatDateTime, formatRelativeTime } from '../utils/formatting';

function downloadCsv(prospects: ProspectResponse[], filename: string) {
  const headers = [
    'Organization',
    'Name',
    'Email',
    'Job Title',
    'Phone Number',
    'Specialty',
    'Location',
    'MSO Platforms',
    'MSO Count',
    'Services/Modalities',
    'Insurance Types',
    'Insurance Carriers',
    'Population Focus',
    'Clinical Focus',
    'Owns Solo Practice',
    'In Group Practice',
    'Owns Group Practice',
    'Group Practice Website',
    'Colleague Count',
    'B2C vs B2B',
    'Created At',
    'Updated At',
  ];

  const escapeCSV = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    let str: string;
    if (Array.isArray(value)) {
      str = value.join('; ');
    } else if (typeof value === 'string') {
      str = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      str = String(value);
    } else {
      str = JSON.stringify(value);
    }
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const rows = prospects.map(p => [
    escapeCSV(p.organization),
    escapeCSV(p.name),
    escapeCSV(p.email),
    escapeCSV(p.job_title),
    escapeCSV(p.phone_number),
    escapeCSV(p.specialty_enriched),
    escapeCSV(p.location),
    escapeCSV(p.mso_platforms),
    escapeCSV(p.mso_count),
    escapeCSV(p.services_modalities),
    escapeCSV(p.insurance_types),
    escapeCSV(p.insurance_carriers),
    escapeCSV(p.population_focus),
    escapeCSV(p.clinical_focus),
    escapeCSV(p.owns_solo_practice),
    escapeCSV(p.in_group_practice),
    escapeCSV(p.owns_group_practice),
    escapeCSV(p.group_practice_website),
    escapeCSV(p.colleague_count),
    escapeCSV(p.b2c_vs_b2b),
    escapeCSV(p.created_at),
    escapeCSV(p.updated_at),
  ]);

  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function ProspectsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [organizationInput, setOrganizationInput] = useState(searchParams.get('organization') || '');
  const [emailInput, setEmailInput] = useState(searchParams.get('email') || '');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [allProspectsSelected, setAllProspectsSelected] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [fetchAllProspects] = adminV2Api.useLazyGetProspectsQuery();

  const page = parseInt(searchParams.get('page') || '1', 10);
  const organization = searchParams.get('organization') || undefined;
  const email = searchParams.get('email') || undefined;
  const enrichedParam = searchParams.get('enriched');
  const enriched = enrichedParam === 'true' ? true : enrichedParam === 'false' ? false : undefined;
  const sortBy = searchParams.get('sort_by') || undefined;
  const sortOrder = (searchParams.get('sort_order') as 'asc' | 'desc') || undefined;

  const { data, isLoading, error, refetch } = useGetProspectsQuery({
    page,
    pageSize: 20,
    organization,
    email,
    enriched,
    sortBy,
    sortOrder,
  });

  const handleSearch = useCallback(() => {
    const params = new URLSearchParams();
    if (organizationInput.trim()) {
      params.set('organization', organizationInput.trim());
    }
    if (emailInput.trim()) {
      params.set('email', emailInput.trim());
    }
    if (enrichedParam) {
      params.set('enriched', enrichedParam);
    }
    params.set('page', '1');
    setSearchParams(params);
  }, [organizationInput, emailInput, enrichedParam, setSearchParams]);

  const handleEnrichedFilterChange = useCallback((value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value === '') {
      params.delete('enriched');
    } else {
      params.set('enriched', value);
    }
    params.set('page', '1');
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handlePageChange = useCallback((newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  }, [searchParams, setSearchParams]);

  const handleRowClick = useCallback((prospect: ProspectResponse) => {
    navigate(`/admin-v2/prospect/${prospect.prospect_segment_id}`);
  }, [navigate]);

  const handleUploadSuccess = useCallback(() => {
    setUploadModalOpen(false);
    refetch();
  }, [refetch]);

  const prospects = useMemo(() => data?.prospects || [], [data?.prospects]);

  const handleSelectAllVisible = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(prospects.map(p => p.prospect_segment_id)));
    } else {
      setSelectedIds(new Set());
      setAllProspectsSelected(false);
    }
  }, [prospects]);

  const handleSelectAllProspects = useCallback(() => {
    setAllProspectsSelected(true);
    // Also select all visible for visual feedback
    setSelectedIds(new Set(prospects.map(p => p.prospect_segment_id)));
  }, [prospects]);

  const handleClearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAllProspectsSelected(false);
  }, []);

  const handleSelectOne = useCallback((id: number, checked: boolean) => {
    // If we're in "all selected" mode and unchecking, exit that mode
    if (allProspectsSelected && !checked) {
      setAllProspectsSelected(false);
    }
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, [allProspectsSelected]);

  const handleDownloadSelected = useCallback(async () => {
    const timestamp = todayCalendarDate('UTC');

    if (allProspectsSelected && data?.total_count && data.total_count > prospects.length) {
      // Fetch all prospects across all pages
      setIsDownloading(true);
      try {
        const allProspects: ProspectResponse[] = [];
        const totalPages = data.total_pages || 1;

        for (let p = 1; p <= totalPages; p++) {
          const result = await fetchAllProspects({
            page: p,
            pageSize: 100, // Fetch larger pages for efficiency
            organization,
            email,
            enriched,
            sortBy,
            sortOrder,
          }).unwrap();
          allProspects.push(...result.prospects);
        }

        downloadCsv(allProspects, `prospects-export-all-${timestamp}.csv`);
      } catch (err) {
        console.error('Failed to fetch all prospects:', err);
      } finally {
        setIsDownloading(false);
      }
    } else {
      // Download only selected prospects from current view
      const selectedProspects = prospects.filter(p => selectedIds.has(p.prospect_segment_id));
      if (selectedProspects.length > 0) {
        downloadCsv(selectedProspects, `prospects-export-${timestamp}.csv`);
      }
    }
  }, [allProspectsSelected, data, prospects, selectedIds, fetchAllProspects, organization, email, enriched, sortBy, sortOrder]);

  const allVisibleSelected = prospects.length > 0 && prospects.every(p => selectedIds.has(p.prospect_segment_id));
  const someSelected = prospects.some(p => selectedIds.has(p.prospect_segment_id)) && !allVisibleSelected;
  const totalCount = data?.total_count || 0;
  const hasMorePages = totalCount > prospects.length;

  const columns = useMemo(
    () => [
      {
        key: 'prospect_segment_id' as const,
        label: (
          <Checkbox
            checked={allVisibleSelected || allProspectsSelected}
            indeterminate={someSelected}
            onChange={(e) => handleSelectAllVisible(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        ),
        render: (value: unknown) => (
          <Checkbox
            checked={allProspectsSelected || selectedIds.has(value as number)}
            onChange={(e) => handleSelectOne(value as number, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        ),
      },
      {
        key: 'organization' as const,
        label: 'Organization',
        render: (value: unknown) => (
          <div className="flex items-center">
            <TrendingUp className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{(value as string) || '-'}</span>
          </div>
        ),
      },
      {
        key: 'name' as const,
        label: 'Name',
        render: (value: unknown) => (
          <span className="text-gray-900">{(value as string) || '-'}</span>
        ),
      },
      {
        key: 'email' as const,
        label: 'Email',
        render: (value: unknown) => (
          <span className="text-gray-600">{(value as string) || '-'}</span>
        ),
      },
      {
        key: 'job_title' as const,
        label: 'Job Title',
        render: (value: unknown) => (
          <span className="text-gray-600">{(value as string) || '-'}</span>
        ),
      },
      {
        key: 'specialty_enriched' as const,
        label: 'Status',
        render: (value: unknown) => (
          <Chip
            size="small"
            label={value ? 'Enriched' : 'Pending'}
            color={value ? 'success' : 'warning'}
          />
        ),
      },
      {
        key: 'created_at' as const,
        label: 'Created',
        render: (value: unknown) => {
          if (!value) return <span className="text-gray-400">-</span>;
          const date = new Date(value as string);
          return (
            <div className="text-sm">
              <div className="text-gray-900">{formatDateTime(date)}</div>
              <div className="text-gray-500">{formatRelativeTime(date)}</div>
            </div>
          );
        },
      },
    ],
    [allVisibleSelected, allProspectsSelected, someSelected, selectedIds, handleSelectAllVisible, handleSelectOne]
  );

  if (error) {
    return (
      <div className="min-h-full min-w-0 bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Error loading prospects</h2>
            <p className="text-gray-600 mt-2">Unable to fetch prospects list.</p>
            <Button onClick={() => navigate('/admin-v2')} className="mt-4">
              Back to Search
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full min-w-0 bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>
        <div className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Sales Prospects</h1>
            <div className="flex gap-2">
              {(selectedIds.size > 0 || allProspectsSelected) && (
                <Button
                  variant="outlined"
                  startIcon={isDownloading ? <CircularProgress size={20} /> : <Download />}
                  onClick={handleDownloadSelected}
                  disabled={isDownloading}
                >
                  {isDownloading
                    ? 'Downloading...'
                    : allProspectsSelected
                      ? `Download All (${totalCount})`
                      : `Download Selected (${selectedIds.size})`}
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<CloudUpload />}
                onClick={() => setUploadModalOpen(true)}
              >
                Upload CSV
              </Button>
            </div>
          </div>

          <Card className="mb-6">
            <CardContent>
              <div className="flex flex-wrap gap-4 items-end">
                <TextField
                  label="Organization"
                  placeholder="Filter by organization..."
                  value={organizationInput}
                  onChange={(e) => setOrganizationInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  label="Email"
                  placeholder="Filter by email..."
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                  size="small"
                  sx={{ minWidth: 200 }}
                />
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={enrichedParam || ''}
                    label="Status"
                    onChange={(e) => handleEnrichedFilterChange(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Enriched</MenuItem>
                    <MenuItem value="false">Pending</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>

          {allVisibleSelected && !allProspectsSelected && hasMorePages && (
            <Alert
              severity="info"
              icon={<SelectAll />}
              action={
                <div className="flex gap-2">
                  <Button
                    size="small"
                    onClick={handleSelectAllProspects}
                  >
                    Select all {totalCount} prospects
                  </Button>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={handleClearSelection}
                  >
                    Clear selection
                  </Button>
                </div>
              }
              sx={{ mb: 2 }}
            >
              All {prospects.length} prospects on this page are selected.
            </Alert>
          )}

          {allProspectsSelected && (
            <Alert
              severity="success"
              icon={<SelectAll />}
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleClearSelection}
                >
                  Clear selection
                </Button>
              }
              sx={{ mb: 2 }}
            >
              All {totalCount} prospects are selected.
            </Alert>
          )}

          {isLoading && !data ? (
            <LoadingState message="Loading prospects..." />
          ) : (
            <>
              <DataTable
                title="Prospects"
                columns={columns}
                data={prospects}
                total={data?.total_count}
                loading={isLoading}
                onRowClick={handleRowClick}
                emptyMessage="No prospects found"
              />

              {data && data.total_pages && data.total_pages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                  <Button
                    disabled={page <= 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="py-2 px-4">
                    Page {page} of {data.total_pages}
                  </span>
                  <Button
                    disabled={!data.total_pages || page >= data.total_pages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <ProspectsUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </div>
  );
}
